/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useDispatch} from "useDispatch";
import React, {useEffect, useState} from "react";
import BookingAction, {PaymentAssert} from "store/booking/BookingAction";
import Loader from "components/Loader";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {useTranslation} from "react-i18next";
import {translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import TagManager from "react-gtm-module";
import {ThunkAction} from "redux-thunk";
import VoucherAction from "store/shop/VoucherAction";
import {HallCode} from "types/enums/HallCode";
import {DEFAULT_HALL} from "helpers/config";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import BookedTimeslots from "pages/Front/Booking/components/BookingCustomer/BookedTimeslots";
import BookingPaidContainer from "pages/Front/Booking/BookingPaid/BookingPaidContainer";
import BookingPaidReceipt from "pages/Front/Booking/BookingPaid/BookingPaidReceipt";
import BookingPaidTickets from "pages/Front/Booking/BookingPaid/BookingPaidTickets";
import BookingPaidButtons from "pages/Front/Booking/BookingPaid/BookingPaidButtons";
import BookingPaidCustomer from "pages/Front/Booking/BookingPaid/BookingPaidCustomer";
import BookingPaidCheckin from "pages/Front/Booking/BookingPaid/BookingPaidCheckin";
import BookingPaymentStatus from "pages/Front/Booking/BookingPaid/BookingPaymentStatus";
import {Hall} from "types/entities/Hall";
import {PackagesCart} from "types/entities/PackagesCart";
import {Payment} from "types/entities/Payment";
import {PaymentItem} from "types/entities/PaymentItem";
import {isResultSuccess} from "utils/paymentUtils";
import ReactGA from "react-ga4";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {useCookies} from "utils/hooks/useCookies";
import {PackageCategory} from "types/enums/PackageCategory";

interface Params {
    payment_id: string;
    reference: string;
}

interface BookingPaidProps extends RouteComponentProps<Params> {}

interface BookingContextProps {
    payment: Payment | null;
    paymentAssert: PaymentAssert | null;
    hall: Hall | null;
    packagesCart: PackagesCart | null;
}

export const BookingPaidContext = React.createContext<BookingContextProps | null>(null);

const BookingPaid = (props: BookingPaidProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const packageCart = useTypedSelector(state => state.bookingState.bookingProcess).packageCart;
    const bookingVoucherCodes = packageCart
        ? packageCart.packagesCartItemVouchers &&
          packageCart.packagesCartItemVouchers.length !== 0 &&
          packageCart.packagesCartItemVouchers.map(pciv => pciv.voucher.code)
        : [""];

    const hall = useTypedSelector(state => state.hallState?.hall) || null;

    const [result, setResult] = useState<Payment | null>(null);
    const [assert, setAssert] = useState<PaymentAssert | null>(null);
    const [paymentItems, setPaymentItems] = useState<PaymentItem[]>([]);

    const transactionTotal = result?.amount;
    const {cookiesEnabled, showCookiesBanner} = useCookies();

    // const dataLayer = {
    //     event: "purchase",
    //     action: "Paid",
    //     category: "Booking",
    //     label: "Booking Paid",
    //     valueFormatted: currencyNumberFormat(packageCart?.price),
    //     value: transactionTotal,
    //     transactionTotal: transactionTotal,
    //     userId: result?.customer?.id,
    //     paymentId: result?.id,
    //     paymentStatus: result?.status,
    //     transactionId: result?.reservation_id,
    //     ecommerce: {
    //         purchase: {
    //             actionField: {
    //                 id: result?.reservation_id,
    //                 revenue: transactionTotal,
    //             },
    //             products: [...paymentItems],
    //         },
    //     },
    // };

    const dataLayerItems: DataLayerItem[] = paymentItems.map(item => {
        return {
            item_id: item.article_id, // Change this to the identifier for the item
            item_name: item.article ? translate(item.article.article_name) : "Access ticket", // Change this to the name of the item
            quantity: item.quantity,
            item_variant: item.article?.person_type ?? "adult",
            price: item.price,
            currency: "CHF", // Change it if the currency is different for the item
        };
    });

    const dataLayer = {
        params: {
            action: "purchase",
            category: "ecommerce",
            label: "Booking Paid",
            value: transactionTotal ? transactionTotal / 100 : 0,
            coupon: bookingVoucherCodes ? bookingVoucherCodes.join(" ") : null,
            currency: "CHF", // Assuming the currency is USD, change it if it's different
            transaction_id: result?.reservation_id,
            user_id: result?.customer?.id,
            payment_id: result?.id,
            payment_status: result?.status,
            items: dataLayerItems,
        },
    };

    const sendBookingPaidEvent = () => {
        ReactGA.event("purchase", {
            action: "purchase",
            category: "Ecommerce",
            currency: "CHF",
            label: "Booking Paid",
            transaction_id: result?.id,
            value: transactionTotal ? transactionTotal / 100 : 0,
            items: dataLayerItems,
        });
    };

    useEffect(() => {
        dispatch<ThunkAction<any, any, any, any>>(BookingAction.fetchPaidCart(+props.match.params.payment_id, props.match.params.reference)).then(r => {
            setResult(r.payment);
            const paymentTickets: PaymentItem[] = r.payment?.paymentItems?.flatMap(paymentItem => paymentItem) || [];
            setPaymentItems(paymentTickets);
            setAssert(r.assert);
        });
    }, []);

    useEffect(() => {
        dispatch(VoucherAction.resetVouchers());
        dispatch(VoucherAction.resetAppliedVouchers());
    }, []);

    useEffect(() => {
        console.log(cookiesEnabled, result, paymentItems.length);
        if (cookiesEnabled) {
            if (result) {
                if (paymentItems.length !== 0) {
                    sendBookingPaidEvent();
                }
            }
        }
    }, [result, paymentItems]);

    useEffect(() => {
        if (result && paymentItems && paymentItems.length !== 0) {
            TagManager.dataLayer({dataLayer: dataLayer});
        }
    }, [result, paymentItems]);

    const packagesCartItemsOutstanding =
        result?.reservation &&
        result.reservation.packagesCart?.packagesCartItems &&
        result.reservation.packagesCart.packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
            if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
                return false;
            }
            if (packagesCartItem.packageItem?.activity_id === null) {
                return true;
            }
        });

    const BookingContextValue: BookingContextProps = {
        payment: result,
        paymentAssert: assert,
        hall: hall,
        packagesCart: packageCart,
    };

    if (!result && !assert) {
        return (
            <BookingPaidContainer>
                <BookingPaidButtons />
            </BookingPaidContainer>
        );
    }

    if (!hall) {
        return <Loader />;
    }

    return (
        <BookingPaidContainer>
            <BookingPaidContext.Provider value={BookingContextValue}>
                <BookingPaymentStatus />
                {isResultSuccess(result) && result && result.reservation && <BookingPaidCheckin />}
                {!isResultSuccess(result) && (
                    <CardComponent className={"text-sm sm:text-lg"}>
                        <div className={"flex flex-col justify-center items-center p-4 rounded-md space-y-2 sm:space-y-8"}>
                            <span>{t("Please retry payment again in a few minutes")}</span>
                        </div>
                    </CardComponent>
                )}

                {isResultSuccess(result) && result && result.reservation && (
                    <>
                        {result.reservation &&
                            result.reservation.packagesCart &&
                            result.reservation.packagesCart.packagesCartItemSlots &&
                            result.reservation.packagesCart.packagesCartItemSlots?.length > 0 && (
                                <div className={"BookedTimeslots"}>
                                    <BookedTimeslots
                                        centered={true}
                                        packagesCart={result.reservation.packagesCart}
                                        packagesCartItemsOutstanding={packagesCartItemsOutstanding}
                                        packagesCategory={result.package?.category as PackageCategory}
                                    />
                                </div>
                            )}
                        <BookingPaidCustomer />
                        <BookingPaidReceipt />

                        {DEFAULT_HALL === HallCode.Zwolle && <BookingPaidTickets />}
                    </>
                )}

                <BookingPaidButtons />
            </BookingPaidContext.Provider>
        </BookingPaidContainer>
    );
};

export default withRouter(BookingPaid);
